import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NmibleCountries } from '../../../../../core/constants/nmible-countries';
import { Permissions } from '../../../../../core/constants/permissions';
import { LogHelper } from '../../../../../core/helpers/log.helper';
import {
  AssignNmibleCardRequest,
  CreateNmibleCardRequest,
  FullPatientPaymentInfo,
  NmiblePaymentOption,
  NmiblePaymentOptionType
} from '../../../../../core/models/patient.model';
import { SelectOption } from '../../../../../core/models/select-option.model';
import { AuthService } from '../../../../../core/services/auth.service';
import { PatientService } from '../../../../../core/services/patient.service';
import { AlertService } from '../../../../../shared/alert/alert.service';
import {
  AutosuggestDropdownInputComponent
} from '../../../../../shared/autosuggest-dropdown-input/autosuggest-dropdown-input.component';
import { ConfirmActionModalComponent } from '../../../../../shared/confirm-action-modal/confirm-action-modal.component';
import { HighnoteCardViewerComponent } from './highnote-card-viewer/highnote-card-viewer.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-payment-info-nmible-card',
  templateUrl: './payment-info-nmible-card.component.html',
  styleUrls: ['./payment-info-nmible-card.component.scss']
})
export class PaymentInfoNmibleCardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() patientDetail: FullPatientPaymentInfo;
  @Input() patientTrialId: string;

  @ViewChild("countrySelect") countrySelect: AutosuggestDropdownInputComponent;
  @ViewChild("highnoteCardViewer") highnoteCardViewer: HighnoteCardViewerComponent;
  @ViewChild("confirmRemoveCardModal") confirmRemoveCardModal: ConfirmActionModalComponent;

  Permissions = Permissions;
  canWritePaymentInfo = false;

  countries: SelectOption[] = NmibleCountries.all().map((x) => ({ value: x.code, text: x.name }));

  cardCreationType: 'new' | 'existing' | null = 'new';

  nmibleCardForm = new FormGroup({
    givenName: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z.' ]+(?<![.'])$/)]),
    middleName: new FormControl('', [Validators.pattern(/^[A-Za-z.' ]+(?<![.'])$/)]),
    familyName: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z.' ]+(?<![.'])$/)]),
    country: new FormControl('', [Validators.required]),
    currency: new FormControl({ value: '', disabled: true }),
    email: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/)]),
    consent: new FormControl(false, [Validators.requiredTrue]),
  });

  assignCardForm = new FormGroup({
    patientName: new FormControl('', [Validators.required]),
    sourcePatientTrialId: new FormControl('', [Validators.required]),
    consent: new FormControl(false, [Validators.requiredTrue]),
  });

  patientSuggestions: SelectOption[] = [];

  processing = false;
  loadingCards = false;

  cards: NmiblePaymentOption[] = [];
  selectedCard: NmiblePaymentOption | null = null;
  viewedCard: NmiblePaymentOption | null = null;

  balance: number;
  currency: string;
  cardExpirationDate: string;

  constructor(
    public authService: AuthService,
    private patientService: PatientService,
    private alertService: AlertService
  ) { }

  ngOnInit() {
    this.canWritePaymentInfo = this.authService.hasPermission(Permissions.PatientPaymentInfoWrite);

    if (!this.canWritePaymentInfo) {
      this.cardCreationType = null;
      this.nmibleCardForm.disable();
      this.assignCardForm.disable();
    }

    this.populateForm();
    this.setupCountryChangeListener();
    this.loadCards();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCards() {
    this.loadingCards = true;
    this.patientService.getNmiblePaymentOptions(this.patientTrialId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (options) => {
        this.cards = options.filter(o => o.optionType === NmiblePaymentOptionType.VirtualCard);
        this.selectedCard = this.cards.find(c => c.selected) ?? null;
        this.viewedCard = this.selectedCard;
        this.loadingCards = false;

        if (this.viewedCard) {
          this.getVisualisationDetails(this.viewedCard.optionId);
        }
      },
      error: (err) => {
        LogHelper.error(err);
        this.loadingCards = false;
        this.alertService.showWarningAlert('Failed to fetch virtual cards. Please try again or contact support.');
      }
    });
  }

  viewCard(card: NmiblePaymentOption) {
    this.viewedCard = card;
    this.getVisualisationDetails(card.optionId);
  }

  getVisualisationDetails(optionId?: string) {
    this.patientService.getNmibleCardVisualisationDetails(this.patientTrialId, optionId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.balance = response.balance;
        this.currency = response.currency;
        this.cardExpirationDate = response.expirationDate;

        setTimeout(() => {
          if (this.highnoteCardViewer) {
            this.highnoteCardViewer.visualiseCard(response.clientToken, response.paymentCardId);
          }
        });
      },
      error: (err) => {
        LogHelper.error(err);
        this.alertService.showWarningAlert('Failed to fetch visualisation details. Please try again or contact support.');
      }
    });
  }

  selectCard(optionId: string) {
    this.processing = true;
    this.patientService.selectNmiblePaymentOption(this.patientTrialId, optionId).subscribe({
      next: () => {
        this.processing = false;
        this.loadCards();
        this.alertService.showSuccessAlert('Card selected successfully');
      },
      error: (err) => {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showWarningAlert('Failed to select card. Please try again or contact support.');
      }
    });
  }

  confirmRemoveCard(optionId: string)
  {
    this.confirmRemoveCardModal.showConfirmation(optionId, 'Remove Card', 'Are you sure you want to remove this card?');
  }

  removeCard(optionId: string) {
    this.processing = true;
    this.patientService.removeNmiblePaymentOption(this.patientTrialId, optionId).subscribe({
      next: () => {
        this.processing = false;
        this.loadCards();
        this.alertService.showSuccessAlert('Card removed successfully');
      },
      error: (err) => {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showWarningAlert('Failed to remove card. Please try again or contact support.');
      }
    });
  }

  assignNmibleVirtualCard() {
    const request: AssignNmibleCardRequest = {
      sourcePatientTrialId: this.assignCardForm.get('sourcePatientTrialId').value,
      targetPatientTrialId: this.patientTrialId
    };

    this.processing = true;
    this.patientService.assignNmibleCard(request).subscribe({
      next: () => {
        this.processing = false;
        this.loadCards();
        this.alertService.showSuccessAlert('Card assigned successfully');
      },
      error: (err) => {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showErrorAlert(err);
      }
    });
  }

  onPatientSelected(option: SelectOption | null) {
    this.assignCardForm.get('sourcePatientTrialId').patchValue(option?.patientTrialId ?? null);
  }

  populateForm() {
    this.nmibleCardForm.patchValue({
      givenName: this.patientDetail.firstName,
      familyName: this.patientDetail.lastName,
      email: this.patientDetail.email
    });
    this.nmibleCardForm.markAllAsTouched();
  }

  setupCountryChangeListener() {
    this.nmibleCardForm.get('country').valueChanges.subscribe((countryCode: string) => {
      if (countryCode) {
        this.onCountryChange(countryCode);
      }
    });
  }

  onCountryChange(countryCode: string) {
    const country = NmibleCountries.all().find(x => x.code === countryCode);
    if (country) {
      this.nmibleCardForm.patchValue({
        currency: country.currency
      });
    }
  }

  createVirtualCard() {
    let request = this.nmibleCardForm.value as CreateNmibleCardRequest;
    request.patientTrialId = this.patientTrialId;

    this.processing = true;
    this.patientService.createNmibleCard(request).subscribe({
      next: () => {
        this.processing = false;
        this.loadCards();
        this.alertService.showSuccessAlert('Card created successfully');
      },
      error: (err) => {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showWarningAlert('Failed to create card. Please try again or contact support.');
      }
    });
  }
}
